import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { perfilamientoService } from '@/features/perfilamiento/services/perfilamiento.service'
import type { FilterPerfilamientoResponse, StartPerfilamientoResponse } from '@/features/perfilamiento/services/perfilamiento.types'
import {
  loadPerfilamientoFilterQuestion,
  persistPerfilamientoQuestionnaireQuestions,
} from '@/features/perfilamiento/store/perfilamiento.store'
import { Button } from '@/shared/components/ui/Button'
import { usePerfilamientoError } from '@/features/perfilamiento/components/PerfilamientoErrorModal'
import { CheckCircleIcon } from '@/shared/components/ui/icons/CheckCircleIcon'
import logoNegroSrc from '@/assets/LogoNegro.png'

type FilterLocationState = {
  filterQuestion?: StartPerfilamientoResponse['filterQuestion']
}

type ModalState = {
  visible: boolean
  message?: string
}

export function PerfilamientoFilterPage() {
  const { processId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('perfilamiento')
  const { showError: reportError } = usePerfilamientoError()
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modal, setModal] = useState<ModalState>({ visible: false })
  const [question, setQuestion] = useState<StartPerfilamientoResponse['filterQuestion'] | null>(
    (location.state as FilterLocationState | null)?.filterQuestion ?? null,
  )

  useEffect(() => {
    if (!processId) return

    const currentProcessId = processId

    async function loadQuestion() {
      const storedQuestion = loadPerfilamientoFilterQuestion(currentProcessId)

      if (question) return

      if (storedQuestion) {
        setQuestion(storedQuestion)
        return
      }

      const result = await perfilamientoService.getProceso(currentProcessId, i18n.language)

      if (!result.success) {
        setSubmitError(result.message)
        reportError(result.message)
        return
      }

      const currentQuestion = result.data.process.questions[0] ?? null

      if (!currentQuestion) {
        setSubmitError(t('filter.error.unavailable'))
        return
      }

      persistPerfilamientoQuestionnaireQuestions(
        currentProcessId,
        result.data.process.questions,
      )
      setQuestion(currentQuestion)
    }

    void loadQuestion()
  }, [i18n.language, processId, question, reportError, t])

  async function handleSubmit() {
    if (!processId || !question || !selectedOption) {
      setShowError(true)
      return
    }

    setIsSubmitting(true)
    setShowError(false)

    const result = await perfilamientoService.filter(
      processId,
      { optionId: selectedOption },
      i18n.language,
    )

    setIsSubmitting(false)

    if (!result.success) {
      setSubmitError(result.message)
      reportError(result.message)
      return
    }

    const payload = result.data as FilterPerfilamientoResponse

    if (payload.action === 'advisory') {
      setModal({
        visible: true,
        message: t('filter.advisoryDescription'),
      })
      return
    }

    if (payload.action === 'questionnaire') {
      persistPerfilamientoQuestionnaireQuestions(
        processId,
        payload.questions ?? [],
      )
      navigate(`/perfilamiento/cuestionario/${processId}`, {
        replace: true,
        state: { questions: payload.questions ?? [] },
      })
    }
  }

  function handleAcceptAdvisory() {
    // /filter ya crea la solicitud cuando la opción deriva a asesoría.
    // No volver a llamar /advisory: esa segunda llamada provoca ADVISORY_DUPLICATE.
    setModal({ visible: false })
    navigate('/dashboard', { replace: true })
  }

  return (
    <section className="flex h-[100svh] w-full justify-center overflow-y-auto px-4 py-4 md:px-8 md:py-6">
      <div className="flex w-full max-w-[848px] flex-col items-center">
        <div className="w-[200px] text-[var(--color-text-primary)] md:w-[286px]">
          <img alt="SynerGy" className="h-auto w-full" src={logoNegroSrc} />
        </div>

        <p className="mt-5 text-center text-[1.125rem] font-medium leading-[1.5rem] text-[#1a191a] md:mt-6 md:text-[1.375rem] md:leading-[1.875rem]">
          {t('filter.progress')}
        </p>

        <div className="mt-6 h-3 w-full rounded bg-[#f4f4f4] md:mt-8">
          <div className="h-3 w-[89.37%] rounded bg-[var(--color-brand-primary-active)]" />
        </div>

        <h1 className="mt-6 max-w-[52.8125rem] text-center text-[1.625rem] font-semibold leading-[2rem] text-[var(--color-text-primary)] md:mt-8 md:text-[2.25rem] md:leading-[2.75rem]">
          {question?.text ?? t('filter.question')}
        </h1>

        <div className="mt-6 flex w-full shrink-0 flex-col gap-4 md:mt-8 md:gap-5">
          {(question?.options ?? []).map((option) => {
            const isSelected = selectedOption === option.optionId

            return (
              <button
                key={option.optionId}
                className={[
                  'flex min-h-[3.75rem] w-full items-center justify-between rounded-lg border px-4 py-4 text-left transition-colors duration-150 md:min-h-[4rem] md:py-4',
                  isSelected
                    ? 'border-[var(--color-brand-primary)] bg-[var(--color-surface-accent-soft)]'
                    : 'border-transparent bg-[#f4f4f4]',
                  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-focus-ring)]',
                ].join(' ')}
                onClick={() => {
                  setSelectedOption(option.optionId)
                  setShowError(false)
                }}
                type="button"
              >
                <span className="max-w-[calc(100%-2rem)] text-sm font-normal leading-[1.125rem] text-[#05141f] md:text-base md:leading-5">
                  {option.text}
                </span>
                <span
                  aria-hidden="true"
                  className={[
                    'inline-flex h-5 w-5 items-center justify-center rounded-full border bg-white transition-colors',
                    isSelected
                      ? 'border-[var(--color-brand-primary)]'
                      : 'border-[#d7d7d7]',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      isSelected ? 'bg-[var(--color-brand-primary)]' : 'bg-transparent',
                    ].join(' ')}
                  />
                </span>
              </button>
            )
          })}

          {showError ? (
            <p className="w-full text-left text-sm font-medium leading-5 text-[#e02d3c]">
              {t('filter.error.required')}
            </p>
          ) : null}
        </div>

        {submitError ? (
          <p className="mt-2 w-full text-center text-sm font-medium leading-5 text-[#e02d3c]">
            {submitError}
          </p>
        ) : null}

        {!question && !submitError ? (
          <p className="mt-2 w-full text-center text-sm font-medium leading-5 text-[var(--color-text-muted)]">
            {t('filter.loadingFallback')}
          </p>
        ) : null}

        <Button
          className="mt-6 h-[48px] w-full max-w-[353px]"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
        >
          {t('filter.next')}
        </Button>

        <span className="sr-only">process {processId ?? 'nuevo'}</span>
      </div>

      {modal.visible ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(5,20,31,0.45)] px-4">
          <div className="w-full max-w-[340px] rounded-[12px] bg-[#EEF7FC] px-8 py-8 text-center shadow-[0_20px_60px_rgba(5,20,31,0.18)]">
            <div className="mx-auto flex h-9 w-9 scale-[2.25] items-center justify-center">
              <CheckCircleIcon />
            </div>

            <h2 className="mt-5 text-[26px] font-semibold leading-6 text-[var(--color-text-primary)]">
              {t('filter.advisoryTitle')}
            </h2>

            <p className="mt-4 text-[16px] leading-6 text-black">
              {modal.message ?? t('filter.advisoryDescription')}
            </p>

            <Button
              className="mt-12 !bg-[#1FC16B]"
              onClick={() => void handleAcceptAdvisory()}
              disabled={isSubmitting}
            >
              {t('filter.advisoryAccept')}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
